import { rtkApi } from './rtkApi';
import type { DashboardData } from '../types/DashboardTypes';

export interface DashboardUsersResponse {
  users: import('../types/DashboardTypes').UserData[];
  totalUsers: number;
}

export interface DashboardAssessmentsResponse {
  assessments: import('../types/DashboardTypes').AssessmentData[];
  totalAssessments: number;
}

export interface DashboardJobOpeningsResponse {
  jobOpenings: import('../types/DashboardTypes').JobOpeningData[];
  total: number;
}

export interface DashboardListParams {
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Dashboard RTK Query API — Home pilot.
 * Read-only overview + paginated lists.
 * providesTags per list (LIST + search/page scoped) so future mutations
 * (e.g. job-opening create/update) can invalidate related dashboard tags.
 */
export const dashboardApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardOverview: builder.query<DashboardData, void>({
      query: () => '/dashboard',
      providesTags: [{ type: 'Dashboard', id: 'OVERVIEW' }],
    }),
    getDashboardUsers: builder.query<DashboardUsersResponse, DashboardListParams>({
      query: ({ search = '', page = 1, limit = 5 }) =>
        `/dashboard/user?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
      providesTags: (result, _error, arg) => [
        { type: 'DashboardUsers', id: 'LIST' },
        { type: 'DashboardUsers', id: `${arg.search}-${arg.page}-${arg.limit}` },
      ],
    }),
    getDashboardAssessments: builder.query<DashboardAssessmentsResponse, DashboardListParams>({
      query: ({ search = '', page = 1, limit = 5 }) =>
        `/dashboard/assessment?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
      providesTags: (result, _error, arg) => [
        { type: 'DashboardAssessments', id: 'LIST' },
        { type: 'DashboardAssessments', id: `${arg.search}-${arg.page}-${arg.limit}` },
      ],
    }),
    getDashboardJobOpenings: builder.query<DashboardJobOpeningsResponse, DashboardListParams>({
      query: ({ search = '', page = 1, limit = 5 }) =>
        `/dashboard/job-openings?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
      providesTags: (result, _error, arg) => [
        { type: 'DashboardJobOpenings', id: 'LIST' },
        { type: 'DashboardJobOpenings', id: `${arg.search}-${arg.page}-${arg.limit}` },
      ],
    }),
  }),
});

export const {
  useGetDashboardOverviewQuery,
  useGetDashboardUsersQuery,
  useGetDashboardAssessmentsQuery,
  useGetDashboardJobOpeningsQuery,
} = dashboardApi;
